import { AppText } from "@toothfairy/shared-ui";
import AppSecureTextInput from "@toothfairy/shared-ui/AppSecureTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  useWindowDimensions,
  TextInput,
} from "react-native";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";
import AutoRechargeControl from "./AutoRechargeControl";
import { isElectronRunning } from "../../../App";
import {
  MIN_RECHARGE_USD,
  RECHARGE_PACKS_USD,
  RECHARGE_SLIDER_MAX_USD,
  MAX_RECHARGE_USD,
  formatUoI,
  formatUsd,
} from "../../Constants/billing";

// RechargeSheet: choose a preset pack or a custom amount (min $5), optionally
// enable auto-recharge, then confirm. Reaches the credits contract via the
// `useCredits` instance passed in (recharge + autoRecharge).
const RechargeSheet = ({
  isModalVisible,
  closeModal,
  credits, // useCredits() instance
  onRecharged,
  showAutoRecharge = false,
  titleKey = "credits.recharge.title",
  confirmKey = "credits.recharge.confirm",
  returnTo,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const AppToasty = AppToast.useToast();

  const processingRef = useRef(false);
  const [amount, setAmount] = useState(RECHARGE_PACKS_USD[0]);
  const [customAmount, setCustomAmount] = useState("");
  // Stable seed for the slider — drives initialValue. Updated only on pack
  // pick / sheet open, NEVER on the drag itself (otherwise the dragged value
  // round-trips back into initialValue and fights the slider's internal state).
  const [sliderSeed, setSliderSeed] = useState(RECHARGE_PACKS_USD[0]);
  // Guard: when we change sliderSeed programmatically (pack pick / open), the
  // slider's mount/seed effect fires onSliderValueChange — which would re-set
  // customAmount and undo the pack selection highlight. This ref lets us ignore
  // that programmatic callback so only a real user drag updates customAmount.
  const ignoreSliderCb = useRef(false);
  const [autoRecharge, setAutoRecharge] = useState(
    credits?.autoRecharge || { enabled: false, thresholdUsd: 5, amountUsd: 25 },
  );

  // Initialize from the saved config only when the sheet opens. We must NOT
  // re-sync on every credits refresh: the toggle is a local edit that is only
  // persisted on confirm, so syncing from credits.autoRecharge (which still
  // holds the old saved value) would flip the toggle back off on each poll.
  useEffect(() => {
    if (isModalVisible) {
      setAutoRecharge(
        credits?.autoRecharge || {
          enabled: false,
          thresholdUsd: 5,
          amountUsd: 25,
        },
      );
      setAmount(RECHARGE_PACKS_USD[0]);
      setCustomAmount("");
      ignoreSliderCb.current = true;
      setSliderSeed(RECHARGE_PACKS_USD[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible]);

  const effectiveAmount = customAmount ? Number(customAmount) : amount;
  const isValid =
    effectiveAmount &&
    effectiveAmount >= MIN_RECHARGE_USD &&
    effectiveAmount <= MAX_RECHARGE_USD;

  const handleConfirm = async () => {
    if (processingRef.current) return;
    if (!isValid) {
      const overMax = effectiveAmount && effectiveAmount > MAX_RECHARGE_USD;
      AppToasty.show(
        overMax
          ? t("credits.recharge.maxAmountError", {
              max: MAX_RECHARGE_USD,
              defaultValue: `Maximum recharge is $${MAX_RECHARGE_USD}.`,
            })
          : t("credits.recharge.minAmountError", { min: MIN_RECHARGE_USD }),
        { type: "danger" },
      );
      return;
    }
    processingRef.current = true;
    try {
      if (showAutoRecharge) {
        try {
          await credits?.updateAutoRecharge(autoRecharge);
        } catch (arErr) {
          // non-blocking
        }
      }
      const isElectron = isElectronRunning();
      AppToasty.show(
        isElectron
          ? t("credits.recharge.openingInBrowser", {
              defaultValue: "Opening checkout in your browser…",
            })
          : t("credits.recharge.redirecting"),
        { type: "normal" },
      );
      const result = await credits?.recharge({ amountUsd: effectiveAmount, returnTo });
      const redirectUrl = result?.data?.url || result?.url;
      if (redirectUrl) {
        onRecharged?.(effectiveAmount);
        closeModal?.();
      } else {
        AppToasty.show(t("credits.recharge.failed"), { type: "danger" });
      }
    } catch (e) {
      AppToasty.show(t("credits.recharge.failed"), { type: "danger" });
    } finally {
      processingRef.current = false;
    }
  };

  return (
    <ToothFairyModal
      isModalVisible={isModalVisible}
      setIsModalOpen={(v) => !v && closeModal?.()}
      closeModal={() => closeModal?.()}
      modalStyle={{
        width: width > 600 ? 480 : "95%",
        maxWidth: 520,
        margin: "auto",
        height: "auto",
      }}
      scrollViewStyle={{ margin: 20 }}
      modalTitle={t(titleKey)}
    >
      <View style={{ padding: 16 }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: currentTheme?.theme?.softBackgroundColor,
            borderRadius: 10,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <View>
            <AppText
              style={{
                fontSize: 12,
                color: currentTheme?.theme?.secondary_font_color,
              }}
            >
              {t("credits.recharge.currentBalance")}
            </AppText>
            <AppText style={{ fontSize: 20, fontWeight: "bold" }}>
              {formatUsd(credits?.balanceUsd || 0)}
            </AppText>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <AppText
              style={{
                fontSize: 12,
                color: currentTheme?.theme?.secondary_font_color,
              }}
            >
              {t("credits.recharge.uoiBalance")}
            </AppText>
            <AppText
              style={{
                fontSize: 16,
                fontWeight: "bold",
                color: currentTheme?.theme?.primary,
              }}
            >
              {formatUoI(credits?.balanceUoI || 0)} UoI
            </AppText>
          </View>
        </View>

        <AppText style={{ fontWeight: "bold", marginBottom: 8 }}>
          {t("credits.recharge.chooseAmount")}
        </AppText>
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
          }}
        >
          {RECHARGE_PACKS_USD.map((pack) => (
            <TouchableOpacity
              key={pack}
              onPress={() => {
                setAmount(pack);
                setCustomAmount("");
                // Sync the slider thumb to the pack without letting its
                // callback re-pollute customAmount.
                ignoreSliderCb.current = true;
                setSliderSeed(pack);
              }}
              style={[
                styles.pack,
                !customAmount && amount === pack
                  ? {
                      borderColor: currentTheme?.theme?.primary,
                      borderWidth: 2,
                    }
                  : { borderColor: currentTheme?.theme?.borderColor },
              ]}
            >
              <AppText style={{ fontWeight: "bold" }}>
                {formatUsd(pack)}
              </AppText>
            </TouchableOpacity>
          ))}
        </View>

        <AppText style={{ fontWeight: "bold", marginBottom: 8 }}>
          {t("credits.recharge.customAmount")}
        </AppText>

        {/* Precise / large amount entry. Clamped to the absolute recharge cap;
           writing here takes precedence over the pack + slider selection. */}
        <View
          style={[
            styles.customInput,
            { borderColor: currentTheme?.theme?.border_color },
          ]}
        >
          <AppText style={styles.customInputPrefix}>
            {t("credits.units.usd", { defaultValue: "$" })}
          </AppText>
          <TextInput
            value={customAmount}
            onChangeText={(v) => {
              const cleaned = String(v || "").replace(/[^0-9.]/g, "");
              setCustomAmount(cleaned);
            }}
            keyboardType="numeric"
            placeholder={t("credits.recharge.customAmountPlaceholder", {
              max: MAX_RECHARGE_USD,
              defaultValue: `Up to $${MAX_RECHARGE_USD}`,
            })}
            placeholderTextColor={
              currentTheme?.theme?.secondary_font_color
            }
            style={[
              styles.customInputText,
              { color: currentTheme?.theme?.font_color },
            ]}
          />
        </View>

        {/* Quick slider scoped to common top-up range so the packs visibly move
           the thumb. Dragging sets a custom amount within this range; amounts
           above the slider max are entered via the text input above. */}
        <View style={{ marginBottom: 4 }}>
          <AppSecureTextInput
            key="recharge-slider"
            inputType="SLIDER"
            showTextInputLabel={false}
            isEditable
            showEditButtons={false}
            initialValue={sliderSeed}
            onSliderValueChange={(v) => {
              if (ignoreSliderCb.current) {
                ignoreSliderCb.current = false;
                return;
              }
              const val = Math.max(MIN_RECHARGE_USD, Number(v) || 0);
              setCustomAmount(String(val));
            }}
            minimumValue={MIN_RECHARGE_USD}
            maximumValue={RECHARGE_SLIDER_MAX_USD}
            step={5}
            unitLabel={t("credits.units.usd", { defaultValue: "$" })}
            wrapperStyle={{ width: "100%" }}
            coreViewStyle={{ minHeight: 40 }}
          />
        </View>
        <AppText
          style={{
            fontSize: 11,
            color: currentTheme?.theme?.secondary_font_color,
          }}
        >
          {t("credits.recharge.minAmountHint", {
            min: MIN_RECHARGE_USD,
            max: MAX_RECHARGE_USD,
          })}
        </AppText>

        {showAutoRecharge && (
          <AutoRechargeControl
            value={autoRecharge}
            onDraftChange={setAutoRecharge}
          />
        )}

        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <ToothFairyButton
            isSecondary
            paddingLeft={10}
            paddingRight={10}
            fontColor={currentTheme?.theme?.primary}
            onPress={() => closeModal?.()}
            title={t("common.cancel")}
          />
          <ToothFairyButton
            onPress={handleConfirm}
            paddingLeft={10}
            paddingRight={10}
            isDisabled={!isValid || credits?.recharging}
            title={
              credits?.recharging
                ? t("credits.recharge.processing")
                : t(confirmKey)
            }
          />
        </View>
      </View>
    </ToothFairyModal>
  );
};

const styles = StyleSheet.create({
  pack: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    minWidth: 70,
    alignItems: "center",
    justifyContent: "center",
  },
  customInput: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
    minHeight: 44,
  },
  customInputPrefix: {
    fontWeight: "bold",
    marginRight: 6,
  },
  customInputText: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 8,
    outlineWidth: 0,
  },
});

export default RechargeSheet;
